import { useMemo } from 'react'

import { IconType } from '@factorialco/f0-react'
import type { MenuCategory } from '@factorialco/f0-react/dist/experimental'
import { ModulesEnum } from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { sidebarInfo } from 'modules/core/lib/manifests/manager'

// New sidebar
const sidebarCategories = [
  'root',
  'personal',
  'company',
  'operations',
  'talent',
  'finance',
  'more',
] as const

export type SidebarCategory = (typeof sidebarCategories)[number]

export type SidebarCategoryGroup = {
  identifier: SidebarCategory
  label: string
  isSortable: boolean
  isRoot?: boolean
}

const SidebarCategoryGroups = (): SidebarCategoryGroup[] => [
  {
    identifier: 'root',
    label: '',
    isSortable: false,
    isRoot: true,
  },
  {
    identifier: 'personal',
    label: i18n.t('sidebar.categories.personal'),
    isSortable: true,
  },
  {
    identifier: 'company',
    label: i18n.t('sidebar.categories.company'),
    isSortable: true,
  },
  {
    identifier: 'operations',
    label: i18n.t('sidebar.categories.operations'),
    isSortable: true,
  },
  {
    identifier: 'talent',
    label: i18n.t('sidebar.categories.talent'),
    isSortable: true,
  },
  {
    identifier: 'finance',
    label: i18n.t('sidebar.categories.finance'),
    isSortable: true,
  },
  {
    identifier: 'more',
    label: i18n.t('sidebar.categories.more'),
    isSortable: true,
  },
]

export interface ManifestSidebarItem {
  identifier: string
  name: string
  factorialOneIcon: IconType
  path: string
  group: SidebarCategory
  module?: ModulesEnum
  priority?: 'highest' | 'high' | 'medium' | 'low' | 'lowest'
}

const COLLAPSED_CATEGORIES_KEY = 'factorial_sidebar_collapsed_categories'
const SORTED_CATEGORIES_KEY = 'factorial_sidebar_sorted_categories'

const getUserStorageKey = (baseKey: string): string => {
  try {
    // Get from session storage to avoid re-renders
    const userKey = sessionStorage.getItem('current_user_id')
    return userKey ? `${userKey}_${baseKey}` : baseKey
  } catch {
    return baseKey
  }
}

const getCollapsedCategories = (): string[] => {
  try {
    const key = getUserStorageKey(COLLAPSED_CATEGORIES_KEY)
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export const setCollapsedCategory = (categoryId: string) => {
  const collapsedCategories = getCollapsedCategories()
  const isCollapsed = !collapsedCategories.includes(categoryId)
  const newCollapsedCategories = isCollapsed
    ? [...collapsedCategories, categoryId]
    : collapsedCategories.filter((id) => id !== categoryId)

  const key = getUserStorageKey(COLLAPSED_CATEGORIES_KEY)
  localStorage.setItem(key, JSON.stringify(newCollapsedCategories))
}

const getSortedCategories = (): string[] => {
  try {
    const key = getUserStorageKey(SORTED_CATEGORIES_KEY)
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}
export const setSortedCategories = (categories: MenuCategory[]) => {
  const sortedIds = categories.map((category) => category.id)
  const key = getUserStorageKey(SORTED_CATEGORIES_KEY)
  localStorage.setItem(key, JSON.stringify(sortedIds))
}

export const useSidebarModules = () => {
  const { navigation } = useNavigation()
  return compact(sidebarInfo(navigation).map((item: ManifestSidebarItem) => item.module))
}

export const useSidebarItemByIdentifier = (): Record<
  NonNullable<ManifestSidebarItem['identifier']>,
  ManifestSidebarItem
> => {
  const { navigation } = useNavigation()

  const sidebarItemsByIdentifier = useMemo(() => {
    const sidebarItems = sidebarInfo(navigation)

    return sidebarItems.reduce<
      Record<NonNullable<ManifestSidebarItem['identifier']>, ManifestSidebarItem>
    >((acc, item: ManifestSidebarItem) => {
      // We assume that `identifier` from sidebar manifest match `module` field in routes files
      acc[item.identifier] = item
      return acc
    }, {})
  }, [navigation])

  return sidebarItemsByIdentifier
}

export const useMenuTree = (availableModules: string[]) => {
  const { navigation } = useNavigation()
  const collapsedCategories = getCollapsedCategories()
  const sortedCategories = getSortedCategories()

  return SidebarCategoryGroups()
    .map((category: SidebarCategoryGroup) => {
      return {
        id: category.identifier,
        title: category.label,
        isOpen: !collapsedCategories.includes(category.identifier),
        isRoot: category.isRoot ?? false,
        isSortable: category.isSortable ?? false,
        items: sidebarInfo(navigation)
          .filter((item: ManifestSidebarItem) => {
            const matchesCategory = item.group === category.identifier
            const isAvailableModule = !item.module || availableModules.includes(item.module)

            return matchesCategory && isAvailableModule
          })
          .map((item: ManifestSidebarItem) => {
            return {
              label: item.name,
              href: item.path,
              icon: item.factorialOneIcon,
              priority: item.priority || 'low',
            }
          })
          .sort((a, b) => {
            const priorityOrder = { highest: 0, high: 1, medium: 2, low: 3, lowest: 4 }
            const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]

            // If priorities are different, sort by priority
            if (priorityDiff !== 0) return priorityDiff

            // If priorities are the same, sort alphabetically
            return a.label.localeCompare(b.label)
          }),
      }
    })
    .filter((category) => {
      return category.items.length > 0
    })
    .sort((a, b) => {
      const aIndex = sortedCategories.indexOf(a.id)
      const bIndex = sortedCategories.indexOf(b.id)
      return aIndex - bIndex
    })
}

export const useCommandBarMenuItems = (availableModules: string[]) => {
  const menuCategories = useMenuTree(availableModules)
  return menuCategories.flatMap((category) => {
    return category.items
  })
}
